import { useState, useEffect } from "react";

import SideNavigation from "../Common/sideNavigation";
import SearchBar from "../Common/searchBar";
import Pagination from "../Common/pagination";
import ProjectLogRow from "./projectLogRow";

import { API, apiClient } from "../../../Common/lib/api";

/**
 * Returns the Project Logs page component.
 *
 * @returns The Project Logs page component.
 */
const ProjectLogs = () => {
  const [projectLogs, setProjectLogs] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [noSearchResults, setNoSearchResults] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [projectPerPage, setProjectPerPage] = useState(10);
  const [projectLogCount, setProjectLogCount] = useState(0);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    getLogs();
  }, [currentPage]);

  /**
   * Retrieves the edit logs from the Administration table. Applies necessary filters if a search term has been provided.
   * Also retrieves cognito users using the getUsers method if they havent already been retrieved.
   */
  const getLogs = async () => {
    try {
      const params = {
        ordering: "-Date",
        offset: currentPage * projectPerPage,
        limit: projectPerPage,
      };

      if (searchTerm) {
        const searchDate = getSearchDate();
        if (searchDate && searchDate.createdAt) {
          params.createdAt = searchDate.createdAt;
        }
        params.q = searchTerm;
        
        const usernameFilters = nameToID();
        if (usernameFilters.length > 0) {
          const userIds = usernameFilters.map(f => f.userID?.eq).filter(Boolean);
          if (userIds.length > 0) {
            params.userID__in = userIds.join(',');
          }
        }
      }

      const result = await API.logs.list(params);
      const items = result.data.listLogs.items || [];
      const total = result.data.listLogs.total || 0;

      setProjectLogCount(total);
      setProjectLogs(items);

      if (users.length < 1) {
        getUsers();
      }
    } catch (error) {
      // console.log(error);
    }
  };

  /**
   * Retrieves users.
   */
  const getUsers = async () => {
    try {
      const response = await apiClient.get('/admin/users/');
      setPageLoading(false);
      setUsers(response.data.Users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setPageLoading(false);
      setUsers([]);
    }
  };

  /**
   * Turns the searchTerm variable into a properly formatted date to be used in search filtering.
   * @returns The search filter.
   */
  const getSearchDate = () => {
    try {
      let date = searchTerm.split("/");
      let reformattedDate = "";

      if (date[0].length === 1) {
        date[0] = "0" + date[0];
      }

      if (date[1].length === 1) {
        date[1] = "0" + date[1];
      }

      reformattedDate = date[2] + "-" + date[0] + "-" + date[1];

      return { createdAt: reformattedDate };
    } catch { }
  };

  /**
   * Takes the searchTerm and checks if it matches the first, last, or full names of any user.
   * @returns The filters for matching usernames.
   */
  const nameToID = () => {
    let name = searchTerm;

    try {
      const splitName = name.split(" ");

      const getAttr = (user, attrName) => {
        if (!user || !Array.isArray(user.Attributes)) return '';
        const a = user.Attributes.find((attribute) => attribute && attribute.Name === attrName);
        return a?.Value || '';
      };

      const exactMatchUser = users.find((user) => {
        const given = getAttr(user, 'given_name').toLowerCase();
        const family = getAttr(user, 'family_name').toLowerCase();
        return (
          splitName[0] && given && splitName[0].toLowerCase().includes(given) &&
          splitName[1] && family && splitName[1].toLowerCase().includes(family)
        );
      });

      if (exactMatchUser) return [{ userID: { eq: exactMatchUser.Username } }];
    } catch { }

    const getAttr = (user, attrName) => {
      if (!user || !Array.isArray(user.Attributes)) return '';
      const a = user.Attributes.find((attribute) => attribute && attribute.Name === attrName);
      return a?.Value || '';
    };

    const filteredUsers = users.filter((user) => {
      const given = getAttr(user, 'given_name').toLowerCase();
      const family = getAttr(user, 'family_name').toLowerCase();
      const lname = name.toLowerCase();
      return (given && lname.includes(given)) || (family && lname.includes(family));
    });

    let usernameFilters = [];

    filteredUsers.forEach((user) => {
      usernameFilters.push({ userID: { eq: user.Username } });
    });

    return usernameFilters;
  };

  /**
   * Returns the UNIX EPOCH timestamp in milliseconds into a formatted MM/dd/YYYY date.
   *
   * @param {Integer} unix
   * @returns The UNIX EPOCH timestamp in milliseconds into a formatted MM/dd/YYYY date.
   */
  const formatUNIX = (unix) => {
    return new Date(unix).toLocaleDateString("en-US");
  };

  /**
   * Handles the project search input field.
   *
   * @param {Event Handles} event
   */
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  return (
    <>
      <div className="flex flex-col h-fit md:h-screen">
        <div className="h-full bg-gray-50">
          <SideNavigation />

          <main className="lg:pl-72 bg-gray-50 h-full">
            <div className="">
              <div className="px-4 py-10 sm:px-6 lg:px-8 lg:py-6">



                <div className="">
                  <div className="py-6 px-3 w-full md:px-5 md:py-12 bg-white border border-gray-200 md:w-full rounded-lg xl:p-12 shadow-sm">
                    <div className="mt-2 flex justify-between w-full">
                    <h1 className="text-2xl py-2 font-bold leading-tight text-gray-900">
                      Project Logs
                    </h1>
                    <SearchBar
                      searchPurpose="logs"
                      searchTerm={searchTerm}
                      currentPage={currentPage}
                      handleSearch={handleSearch}
                      getProjects={getLogs}
                      setCurrentPage={setCurrentPage}
                    />
                  </div>

                    <main className="overflow-hidden">
                      {pageLoading ? (
                        <div className="text-base p-40 bg-gray-100 place-content-center">
                          <p className="text-center font-medium">Loading</p>
                        </div>
                      ) : (
                        <>
                          {!projectLogCount ? (
                            <div className="text-base p-40 bg-gray-100 place-content-center">
                              <p className="text-center font-medium">No Project Logs Found.</p>
                            </div>
                          ) : (
                            <>
                              <table className="my-4 w-full">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th
                                      scope="col"
                                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:text-base sm:pl-6"
                                    >
                                      Project Name
                                    </th>

                                    <th
                                      scope="col"
                                      className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:text-base sm:table-cell"
                                    >
                                      User
                                    </th>

                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:text-base"
                                    >
                                      Date
                                    </th>

                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:text-base"
                                    >
                                      Action
                                    </th>
                                  </tr>
                                </thead>

                                <tbody className="divide-y divide-gray-200 bg-white">
                                  {projectLogs.map((projectLog) => (
                                    <ProjectLogRow projectLog={projectLog} formatUNIX={formatUNIX} users={users} />
                                  ))}
                                </tbody>
                              </table>

                              <div className="float-right">
                                <Pagination
                                  projectsPerPage={projectPerPage}
                                  currentPage={currentPage}
                                  projectCount={projectLogCount}
                                  setCurrentPage={setCurrentPage}
                                />

                              </div>
                            </>
                          )}
                        </>
                      )}
                    </main>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default ProjectLogs;
